import { Badge, Heading, Table } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { suspectMapping } from "../../../shared-components/fhir-sections/suspects/shared";
import { CodingBadge } from "../../components/coding-badge";
import { HighlightedText } from "../../components/highlighted-text";
import { TableRow } from "../../utils/tables-metadata";
import { SuspectRowData } from "./suspect-rows";

export type SuspectTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  isRowViewed?: (row: TableRow) => boolean;
};

/**
 * Table component for displaying suspect conditions.
 * Suspects are conditions flagged by analysis that may warrant review.
 */
export function SuspectTable({ rows, onRowClick, filterQuery, isRowViewed }: SuspectTableProps) {
  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  if (rows.length === 0) return null;

  const headerStyles = {
    _first: { borderTopLeftRadius: "md" },
    _last: { borderTopRightRadius: "md" },
    bg: "bg.canvas",
    fontWeight: "bold",
    fontSize: "sm",
    color: "fg",
    py: 3,
    px: 4,
  };

  return (
    <>
      <Heading size="md" fontWeight="semibold" color="fg" mb={6}>
        Suspects ({rows.length})
      </Heading>

      <Table.Root size="sm" variant="line" width="100%">
        <Table.Header bg="bg.canvas">
          <Table.Row>
            {["Condition", "Code", "Suspect Group"].map(header => (
              <Table.ColumnHeader
                key={header}
                {...headerStyles}
                display={header === "Code" ? { base: "none", md: "table-cell" } : undefined}
              >
                {header}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => {
            const suspectRow = row as unknown as SuspectRowData;
            const mapping = suspectMapping[suspectRow.group as keyof typeof suspectMapping] || {
              displayName: suspectRow.group,
              logicTooltip: "No additional information available",
            };
            const isViewed = isRowViewed ? isRowViewed(row) : true;

            return (
              <Table.Row
                key={row.id}
                cursor="pointer"
                onClick={() => handleRowClick(row)}
                _hover={{ bg: "interactive.hover" }}
                borderBottom="1px"
                borderColor="border.subtle"
                boxShadow={
                  isViewed
                    ? "inset 3px 0 0 0 var(--chakra-colors-gray-300)"
                    : "inset 3px 0 0 0 var(--chakra-colors-brand-solid)"
                }
                opacity={isViewed ? 0.6 : 1}
              >
                <Table.Cell textStyle="table.cell" py={3} px={4} color="fg.muted">
                  <HighlightedText text={suspectRow.condition} searchQuery={filterQuery} />
                </Table.Cell>
                <Table.Cell
                  textStyle="table.cell"
                  py={3}
                  px={4}
                  color="fg.muted"
                  display={{ base: "none", md: "table-cell" }}
                >
                  {suspectRow.coding ? (
                    <CodingBadge coding={suspectRow.coding} searchQuery={filterQuery} />
                  ) : (
                    <HighlightedText text={suspectRow.code} searchQuery={filterQuery} />
                  )}
                </Table.Cell>
                <Table.Cell textStyle="table.cell" py={3} px={4} color="fg.muted">
                  <Badge
                    colorScheme="blue"
                    variant="subtle"
                    size="sm"
                    cursor="help"
                    textDecoration="underline"
                    textDecorationStyle="dotted"
                    title={mapping.logicTooltip}
                  >
                    <HighlightedText text={mapping.displayName} searchQuery={filterQuery} />
                  </Badge>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </>
  );
}
